import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import apiFetch from "../../../shared/services/apiClient";
import { useToast, ConfirmModal } from "../../../shared/components/SharedUI";
import { getAvisosPago, revisarAvisoPago, deleteAvisoPago } from "../services/portalService";

const PAGE_SIZE = 8;

const ESTADO_COLORS = {
  PENDIENTE: { bg: "rgba(245,158,11,0.1)",  border: "rgba(245,158,11,0.3)",  text: "#f59e0b" },
  VALIDO:    { bg: "rgba(74,222,128,0.1)",  border: "rgba(74,222,128,0.3)",  text: "#4ade80" },
  NO_VALIDO: { bg: "rgba(248,113,113,0.1)", border: "rgba(248,113,113,0.3)", text: "#f87171" },
};

function Badge({ estado }) {
  const c = ESTADO_COLORS[estado] || { bg: "rgba(156,163,175,0.1)", border: "rgba(156,163,175,0.3)", text: "#9ca3af" };
  return (
    <span style={{ background: c.bg, border: `1px solid ${c.border}`, color: c.text, borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 700 }}>
      {estado?.replace("_", " ")}
    </span>
  );
}

function Pagination({ page, total, onChange }) {
  const pages = Math.ceil(total / PAGE_SIZE);
  if (pages <= 1) return null;
  const btnStyle = (disabled) => ({
    padding: "6px 12px", borderRadius: 8, border: "1px solid var(--border-card)",
    background: "var(--bg-card)", color: disabled ? "var(--text-secondary)" : "var(--text-primary)",
    cursor: disabled ? "default" : "pointer", fontSize: 13, opacity: disabled ? 0.4 : 1,
  });
  return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 10, marginTop: 16 }}>
      <button style={btnStyle(page === 1)} disabled={page === 1} onClick={() => onChange(page - 1)}>
        <i className="fa-solid fa-chevron-left" />
      </button>
      <span style={{ fontSize: 13, color: "var(--text-secondary)" }}>{page} / {pages}</span>
      <button style={btnStyle(page === pages)} disabled={page === pages} onClick={() => onChange(page + 1)}>
        <i className="fa-solid fa-chevron-right" />
      </button>
    </div>
  );
}

export default function AdminAvisosPago() {
  const { t } = useTranslation();
  const { showToast, toastEl } = useToast();

  const [avisos,   setAvisos]   = useState([]);
  const [filiales, setFiliales] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [filtro,   setFiltro]   = useState("PENDIENTE");
  const [saving,   setSaving]   = useState(null);
  const [confirm,  setConfirm]  = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Filters
  const [filterFilial,     setFilterFilial]     = useState("");
  const [filterFechaDesde, setFilterFechaDesde] = useState("");
  const [filterFechaHasta, setFilterFechaHasta] = useState("");
  const [page, setPage] = useState(1);

  function load() {
    getAvisosPago().then(d => setAvisos(d.results || d)).catch(() => {}).finally(() => setLoading(false));
  }
  useEffect(() => {
    load();
    apiFetch("/api/residents/filial/")
      .then(r => r.json())
      .then(d => setFiliales(d.results || d))
      .catch(() => {});
  }, []);

  function resetPage() { setPage(1); }

  async function handleRevisar(id, estado) {
    setSaving(id);
    try {
      await revisarAvisoPago(id, estado);
      showToast(estado === "VALIDO" ? t("portal.marcadoValido") : t("portal.marcadoNoValido"), estado === "VALIDO");
      load();
    } catch { showToast(t("common.errorGuardar"), false); }
    finally { setSaving(null); }
  }

  async function handleDelete() {
    if (!confirm) return;
    setDeleting(true);
    try { await deleteAvisoPago(confirm.id); showToast(t("common.eliminado"), true); setConfirm(null); load(); }
    catch { showToast(t("common.errorEliminar"), false); }
    finally { setDeleting(false); }
  }

  const FILTROS = ["PENDIENTE", "VALIDO", "NO_VALIDO", "TODOS"];
  const pendingCount = avisos.filter(a => a.estado === "PENDIENTE").length;

  const filtered = avisos.filter(a => {
    if (filtro !== "TODOS" && a.estado !== filtro) return false;
    if (filterFilial && String(a.filial) !== filterFilial) return false;
    const fecha = a.created_at?.split("T")[0] || "";
    if (filterFechaDesde && fecha < filterFechaDesde) return false;
    if (filterFechaHasta && fecha > filterFechaHasta) return false;
    return true;
  });
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const inputStyle = {
    padding: "7px 10px", borderRadius: 8, border: "1px solid var(--border-input)",
    background: "var(--hover-bg)", color: "var(--text-primary)", fontSize: 13,
    outline: "none", minWidth: 0,
  };

  return (
    <div style={{ padding: "24px" }}>
      {toastEl}
      <ConfirmModal open={!!confirm} name={confirm?.name} onConfirm={handleDelete} onCancel={() => setConfirm(null)} loading={deleting} />

      {/* Breadcrumb */}
      <span style={{ color: "var(--text-secondary)", fontWeight: 400 }}>
        <Link to="/" style={{ textDecoration: "none" }}>
          <span style={{ color: "var(--text-secondary)", fontWeight: 400 }}>Portal Admin /</span>
        </Link>
        <span style={{ color: "var(--text-primary)", fontWeight: 600 }}> {t("portal.avisosPago")}</span>
      </span>

      <h2 className="mt-2 me-2 p-0 title">{t("portal.avisosPago")}</h2>
      <p className="mb-3" style={{ color: "var(--text-muted)" }}>{t("portal.adminAvisosPagoDesc")}</p>

      {/* Status filter tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        {FILTROS.map(f => (
          <button key={f} onClick={() => { setFiltro(f); resetPage(); }} style={{
            padding: "7px 16px", borderRadius: 20, cursor: "pointer",
            fontWeight: 600, fontSize: 13, border: "none",
            background: filtro === f ? "#f97316" : "var(--hover-bg)",
            color: filtro === f ? "#fff" : "var(--text-secondary)",
            display: "flex", alignItems: "center", gap: 6,
          }}>
            {f === "TODOS" ? t("portal.todos") : f.replace("_", " ")}
            {f === "PENDIENTE" && pendingCount > 0 && (
              <span style={{ background: "#f87171", color: "#fff", borderRadius: 99, padding: "1px 6px", fontSize: 10 }}>
                {pendingCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Filter bar */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
        <select value={filterFilial} onChange={e => { setFilterFilial(e.target.value); resetPage(); }}
          style={{ ...inputStyle, flex: "1 1 140px" }}>
          <option value="">{t("portal.todasLasFiliales")}</option>
          {filiales.map(f => <option key={f.id} value={f.id}>{f.idFilial}</option>)}
        </select>
        <input type="date" value={filterFechaDesde} onChange={e => { setFilterFechaDesde(e.target.value); resetPage(); }}
          title="Desde" style={{ ...inputStyle, flex: "1 1 130px" }} />
        <input type="date" value={filterFechaHasta} onChange={e => { setFilterFechaHasta(e.target.value); resetPage(); }}
          title="Hasta" style={{ ...inputStyle, flex: "1 1 130px" }} />
        {(filterFilial || filterFechaDesde || filterFechaHasta) && (
          <button onClick={() => { setFilterFilial(""); setFilterFechaDesde(""); setFilterFechaHasta(""); setPage(1); }} style={{
            padding: "7px 12px", borderRadius: 8, border: "none",
            background: "rgba(249,115,22,0.1)", color: "#f97316",
            cursor: "pointer", fontSize: 12, fontWeight: 700,
          }}>
            <i className="fa-solid fa-xmark" style={{ marginRight: 4 }} />{t("common.limpiar")}
          </button>
        )}
      </div>

      {loading ? (
        <p style={{ textAlign: "center", color: "var(--text-secondary)" }}>{t("common.cargando")}</p>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: "center", color: "var(--text-secondary)", padding: 48 }}>
          <i className="fa-solid fa-inbox" style={{ fontSize: 40, opacity: 0.4, marginBottom: 12, display: "block" }} />
          <p style={{ margin: 0 }}>{t("portal.sinAvisosPago")}</p>
        </div>
      ) : (
        <>
          <div style={{ background: "var(--bg-card)", border: "1px solid var(--border-card)", borderRadius: 12, overflow: "hidden" }}>
            {paged.map((a, i) => (
              <div key={a.id} style={{
                padding: "14px 16px",
                borderBottom: i < paged.length - 1 ? "1px solid var(--border-card)" : "none",
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                  <div>
                    <p style={{ margin: 0, fontWeight: 700, fontSize: 15, color: "var(--text-primary)" }}>{t("portal.filial")}: {a.filial_nombre}</p>
                    <p style={{ margin: "2px 0 0", fontSize: 12, color: "var(--text-secondary)" }}>{a.usuario_nombre} · {a.created_at?.split("T")[0]}</p>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Badge estado={a.estado} />
                    <button onClick={() => setConfirm({ id: a.id, name: `${a.filial_nombre} — ${a.created_at?.split("T")[0]}` })} title={t("common.eliminar")} style={{
                      background: "rgba(248,113,113,0.1)", border: "1px solid rgba(248,113,113,0.3)",
                      borderRadius: 8, padding: "5px 9px", cursor: "pointer", color: "#f87171", fontSize: 12, flexShrink: 0,
                    }}>
                      <i className="fa-solid fa-trash" />
                    </button>
                  </div>
                </div>
                <p style={{ margin: "0 0 10px", fontSize: 13, color: "var(--text-primary)" }}>{a.detalle}</p>
                {a.comprobante_url && (
                  <a href={a.comprobante_url} target="_blank" rel="noreferrer" style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    background: "rgba(249,115,22,0.1)", border: "1px solid rgba(249,115,22,0.3)",
                    color: "#f97316", padding: "6px 12px", borderRadius: 8,
                    textDecoration: "none", fontWeight: 600, fontSize: 12, marginBottom: 10,
                  }}>
                    <i className="fa-solid fa-paperclip" /> {t("portal.verComprobante")}
                  </a>
                )}
                {a.estado === "PENDIENTE" && (
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={() => handleRevisar(a.id, "NO_VALIDO")} disabled={saving === a.id} style={{
                      padding: "7px 14px", borderRadius: 8,
                      background: "rgba(248,113,113,0.1)", border: "1px solid rgba(248,113,113,0.3)",
                      color: "#f87171", fontWeight: 700, fontSize: 13, cursor: "pointer",
                    }}>
                      <i className="fa-solid fa-xmark" style={{ marginRight: 4 }} />{t("portal.noValido")}
                    </button>
                    <button onClick={() => handleRevisar(a.id, "VALIDO")} disabled={saving === a.id} style={{
                      padding: "7px 14px", borderRadius: 8, border: "none",
                      background: "#f97316", color: "#fff", fontWeight: 700, fontSize: 13,
                      cursor: saving === a.id ? "not-allowed" : "pointer", opacity: saving === a.id ? 0.7 : 1,
                    }}>
                      <i className="fa-solid fa-check" style={{ marginRight: 4 }} />{t("portal.valido")}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
          <Pagination page={page} total={filtered.length} onChange={setPage} />
        </>
      )}
    </div>
  );
}
